<template>
    <div class="register page-container">
        <div class="md md-dialog-fullscreen md-transition-in md-dialog">
            <page-header>
                <h3 slot="title" class="title">
                    Sign up to Go1
                </h3>
                <h6 slot="subtitle" class="subtitle">
                    Access thousands of learning items from top content providers.
                </h6>
                <div slot="caption" class="caption">
                    <h4 class="caption-text">
                        Already have an account?
                        <router-link to="/">
                            <a>Log in</a>
                        </router-link>
                    </h4>
                </div>
            </page-header>
            <form-error v-if="failedRegister" :error="error"> </form-error>

            <form role="form">
                <text-input
                    label="Name"
                    type="text"
                    name="fullName"
                    autocomplete="fullName"
                    v-model="fullName"
                    required
                />
                <br>

                <text-input
                    label="Email"
                    type="email"
                    name="email"
                    autocomplete="email"
                    v-model="email"
                    required
                />
                <p class="small text-danger" v-show="email.length > 0 && emailIsValid === false">
                    Must be a valid email address.
                </p>
                <br>

                <text-input
                    label="Company name"
                    type="text"
                    name="companyName"
                    autocomplete="companyName"
                    v-model="companyName"
                    :isLoading="validPortalLoading"
                    @keypress="checkExistingPortal"
                    required
                />
                <p class="small text-muted margin-none" v-show="fullPortalUrl">
                    Your portal URL will be <strong>{{fullPortalUrl}}</strong>
                </p>
                <p class="small text-danger margin-none" v-show="companyName.length > 0 && companyIsValid === false && !validPortalLoading">
                    PORTAL ALREADY EXISTS
                </p>
                <br>

                <v-select v-model="country" :options="countries" label="name" placeholder="Select your country"></v-select>
                <br>

                <text-input
                    label="Password"
                    type="password"
                    name="password"
                    autocomplete="password"
                    v-model="password"
                    required
                />
                <p class="small" v-show="password.length > 0 && passwordIsValid === false">
                    Must include at least 1 letter and be at least 8 characters long
                </p>
                <br>

                <button-submitter
                    @button-clicked="register"
                    :attemptingSubmit="attemptingSubmit"
                    :submitEnabled="submitEnabled"
                    :formSubmit="false"
                    :clickText="clickText"
                    attemptText="Attempting to create Go1 account..."
                />

                <h5 class="text-center mt-5 mb-4 text-tc">
                    By continuing, you agree to Go1’s
                    <a id="termTos"
                       target="_blank"
                       href="https://www.go1.com/terms/customer-terms"
                    >Terms of Use</a>
                    <span>and</span>
                    <a id="termPrivacy"
                       target="_blank"
                       href="https://www.go1.com/terms/privacy-policy"
                    >Privacy Policy</a>.
                </h5>
            </form>
        </div>
    </div>
</template>

<script>
import AuthService from '../../services/AuthService'
import MailService from '../../services/MailService'
import PortalService from '../../services/PortalService'
import OptionService from '../../services/OptionService'
import vSelect from 'vue-select'
import ButtonSubmitter from '../../components/Generic/ButtonSubmitter'
import FormError from '../../components/Generic/FormError'
import sanitiseSubdomain from '../../utils/SubdomainValidator.js'
import PageHeader from '../../components/Generic/PageHeader'
import TextInput from '../Generic/TextInput';

export default {
    name: "Register",

    components: {
        'v-select': vSelect,
        'button-submitter': ButtonSubmitter,
        'form-error': FormError,
        'page-header': PageHeader,
        'text-input': TextInput,
    },

    data() {
        return {
            fullName: "",
            email: "",
            password: "",
            companyName: "",
            phoneNumber: "",
            term_privacy: true,
            term_tos: true,
            term_collection: true,
            country: OptionService.defaultCountry(),
            countries: [],
            industry: OptionService.defaultIndustry(),
            industries: [],
            validPortal: true,
            validPortalLoading: false,
            attemptingSubmit: false,
            failedRegister: false,
            error: {},
            timeout: null,
        }
    },

    created() {
        // get list of countries/industries
        OptionService.countries().then(countries => {
          this.countries = countries;
        });
        OptionService.industries().then(industries => {
          this.industries = industries;
        });
        this.mapDefaults();
    },

    computed: {

        clickText() {
            return `Sign up`
        },

        // oAuth client name
        clientName() {
            return this.$store.state.client_name;
        },

        // compute full portal URL from company name
        fullPortalUrl() {
            let subdomain = sanitiseSubdomain(this.companyName);
            return subdomain ? `${subdomain}.${this.$store.state.portal_root_domain}` : '';
        },

        firstName() {
            return this.fullName.trim().split(' ').shift();
        },

        lastName() {
            return this.fullName.trim().split(' ').pop();
        },

        // boolean to check if the input is valid and the user isn't already trying to submit
        submitEnabled() {
            return this.validInput && !this.attemptingSubmit;
        },

        // get register form credentials
        registerCredentials() {
            // registrations coming from Totara will be handled the old way for the time being ;)
            if (this.$store.state.client_id === 'totara') {
                return {
                    email: this.email,
                    data: {
                        consent: {
                            term_privacy: this.term_privacy ? 1 : 0,
                            term_tos: this.term_tos ? 1 : 0,
                            term_collection: this.term_collection ? 1 : 0
                        }
                    },
                    first_name: this.firstName,
                    industry: this.industry,
                    instance: this.fullPortalUrl,
                    last_name: this.lastName,
                    license: 5,
                    partner: this.$store.state.partner || "totara",
                    password: this.password,
                    phone_number: this.phoneNumber,
                    product: 'premium',
                    regional: this.country.code,
                    site_name: this.companyName
                };
            }

            let payload = {
                creation_path: this.$store.state.creation_path,
                referrer: this.$store.state.client_name,
                customer: {
                    industry: this.industry,
                    name: this.companyName,
                    country: this.country.code
                },
                portal: {
                    domain: this.fullPortalUrl,
                    seats: 5
                },
                user: {
                    consent: {
                        term_privacy: this.term_privacy,
                        term_tos: this.term_tos,
                        term_collection: this.term_collection
                    },
                    email: this.email,
                    first_name: this.firstName,
                    last_name: this.lastName,
                    password: this.password
                }
            };

            // only add partner portal ID to payload if it is set (otherwise #onboard2 will return a 400 for a null value)
            // turn off notifications by default for partner onboarding
            if (this.$store.state.partner_portal_id !== null) {
                payload.partner_portal_id = this.$store.state.partner_portal_id;
                payload.portal.notifications = false;
            }

            // only add phone number to payload if it is set
            if (this.phoneNumber !== '') {
                payload.user.phone = this.phoneNumber;
            }

            return payload;
        },

        // all these terms must be true in order to allow registering
        consentedToTerms() {
            return this.term_tos
                && this.term_privacy
                && this.term_collection;
        },

        // validate input
        validInput() {
            return this.fullName.length > 0
                && this.fullPortalUrl.length > 0
                && this.companyIsValid
                && this.emailIsValid
                && this.passwordIsValid
                && this.country !== null
                && this.industry !== null;
        },

        emailIsValid() {
            return /^\w+([.-]?\w+)+@\w+([.-]?\w+)(\.\w{2,})+$/i.test(this.email);
        },

        passwordIsValid() {
            return this.password.length >= 8;
        },

        companyIsValid() {
            return this.companyName.length > 0 && this.validPortal;
        },
    },

    methods: {
        // get defaults from Vuex
        mapDefaults() {
            this.fullName = this.$store.state.full_name;
            this.email = this.$store.state.email;
            this.companyName = this.$store.state.company_name;
            this.phoneNumber = this.$store.state.phone_number;
            // check if the country supplied from Vuex exists, if so use that
            OptionService.findCountry(this.$store.state.country).then(country => {
              if (country) {
                this.country = country;
              }
            });
            // check if the industry supplied from Vuex exists, if so use that
            OptionService.findIndustry(this.$store.state.industry).then(industry => {
              if (industry) {
                this.industry = industry;
              }
            });
        },

        register() {
            // return early if submit isn't enabled
            if (!this.submitEnabled) {
                return;
            }

            // clear errors
            this.error = {};
            this.failedRegister = false;
            this.attemptingSubmit = true;

            // try to login with given credentials
            return AuthService.register(this.registerCredentials)
                .then(() => {
                    // register successful, now login to get JWT/UUID etc
                    return AuthService.login({
                        username: this.email,
                        password: this.password
                    })
                })
                .then(() => {
                    // notify internal departments of new user onboarding
                    MailService.notify();
                    this.$router.push({name: 'portals'});
                })
                .catch(error => this.handleError(error));
        },

        // catch all errors
        handleError(error) {
            let errorMessage = "";
            if (error && error.response ) {
                if ( error.response.status === 403) {
                  errorMessage = {message: "This email address has already been registered."};
                } else {
                  errorMessage = error.response.data;
                }
            }
            this.failedRegister = true;
            this.attemptingSubmit = false;
            this.error = errorMessage;
            return Promise.reject();
        },

        checkExistingPortal() {
            this.validPortalLoading = true;
            clearTimeout(this.timeout);
            this.timeout = setTimeout(() => {
                PortalService.portal(this.fullPortalUrl)
                    .then(() => {
                        this.validPortal = false;
                    })
                    .catch(() => {
                        this.validPortal = true;
                    })
                    .finally(() => {
                        this.validPortalLoading = false;
                    });
            }, 1000);
        },
    }
}
</script>

<style scoped>
    form {
        padding-bottom: 20px;
    }

    .consent-confirmations {
        padding-top: 20px;
    }
</style>



// WEBPACK FOOTER //
// src/components/Auth/Register.vue